/**
 * PURE derivations for the Combined Report v2 page (COMBINED-V2-DATA-MAP
 * §2). Client-safe — no db/.server imports; never-throw salvage like the
 * sibling display modules. v1 cites pin `d815ac5a`
 * (`$propertyId_.applications_.$applicationId.tsx`).
 */
import type { ReportActionMode } from './derive'
import { deriveVerificationTiles } from './display'
import type {
	ApplicationSummary,
	FamilyScoreSummary,
	GroupMember,
	MemberDocuments,
	MemberVerificationAvailability,
	OpenroomSummary,
	OpenroomSummaryStatus,
} from './types'

export interface FamilyRiskFlagLists {
	red: string[]
	yellow: string[]
}

/** The slice of `family_score_results.apiResponse.predictions[0]` the
 * combined page binds (v1 FamilyScoreData parse, v1:2466-2503). */
export interface FamilyPredictions {
	/** trueTenantFamilyScore — the household 75/100. */
	familyScore: number | null
	rentRange: { min: number; max: number } | null
	guarantorScore: number | null
	guarantorRecommendation: string | null
	relationshipType: string | null
	riskFlags: FamilyRiskFlagLists
	savingsInvestments: number | null
	verificationSummary: {
		id?: { status?: string }
		income?: { status?: string }
		rent?: { status?: string }
	}
	/** Upstream-pending narrative — same gap as the individual page
	 * (FULLSCREEN-DATA-MAP §3); presence-gated in the UI. */
	summary: string | null
}

const toNumber = (value: unknown): number | null => {
	if (typeof value === 'number' && Number.isFinite(value)) return value
	if (typeof value === 'string' && value.trim() !== '') {
		const parsed = Number(value)
		if (Number.isFinite(parsed)) return parsed
	}
	return null
}

const toText = (value: unknown): string | null => {
	if (typeof value === 'string' && value.trim() !== '') return value.trim()
	return null
}

const toFlagMessages = (list: unknown): string[] => {
	if (!Array.isArray(list)) return []
	return list
		.map((entry) => {
			if (typeof entry === 'string') return entry.trim()
			if (entry != null && typeof entry === 'object' && !Array.isArray(entry))
				return toText((entry as Record<string, unknown>).message) ?? ''
			return ''
		})
		.filter(Boolean)
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
	value != null && typeof value === 'object' && !Array.isArray(value)
		? (value as Record<string, unknown>)
		: null

const toStatusSection = (value: unknown): { status?: string } | undefined => {
	const record = asRecord(value)
	if (!record) return undefined
	const status = toText(record.status)
	return status ? { status } : {}
}

/**
 * Never-throw parse of the family payload. v1 read
 * `apiResponse.predictions[0]` and defaulted every field
 * (v1:2466-2503); unknown shapes yield null rather than crashing the
 * page (same salvage posture as parseStepData / report-display).
 */
export function parseFamilyPredictions(
	apiResponse: Record<string, unknown> | null | undefined,
): FamilyPredictions | null {
	const predictions = apiResponse?.predictions
	if (!Array.isArray(predictions) || predictions.length === 0) return null
	const p = asRecord(predictions[0])
	if (!p) return null

	const rentRangeRecord = asRecord(p.rentRange)
	const min = toNumber(rentRangeRecord?.min)
	const max = toNumber(rentRangeRecord?.max)

	const flagsRecord = asRecord(p.riskFlags)
	const financial = asRecord(p.financialSummary)
	const verification = asRecord(p.verificationSummary)

	return {
		familyScore: toNumber(p.trueTenantFamilyScore),
		rentRange: min !== null && max !== null ? { min, max } : null,
		guarantorScore: toNumber(p.guarantorScore),
		guarantorRecommendation: toText(p.guarantorRecommendation),
		relationshipType: toText(p.relationshipType),
		riskFlags: {
			red: toFlagMessages(flagsRecord?.red),
			yellow: toFlagMessages(flagsRecord?.yellow),
		},
		savingsInvestments: toNumber(financial?.savingsInvestments),
		verificationSummary: {
			id: toStatusSection(verification?.idVerification),
			income: toStatusSection(verification?.incomeVerification),
			rent: toStatusSection(verification?.rentVerification),
		},
		summary: toText(p.summary),
	}
}

/**
 * The household title shown on EVERY combined surface — the page header, the
 * breadcrumb, the browser tab title, and the PDF's heading + footer.
 *
 * Client decision 2026-07-28: the combined report shows no person-derived name
 * at all. This replaces `deriveFamilyName`, which built "<Surname> Family" from
 * the primary's display name (the original Combined Report design). That
 * derivation was DELETED rather than left unrendered, so no surface can quietly
 * reintroduce a name — and so the surname rule can't linger in the docs as a
 * behaviour we no longer have.
 */
export const COMBINED_REPORT_TITLE = 'Combined Report'

/**
 * Per-member verification availability for the combined Verification Summary.
 * id/income/rent come from the member's aiReportData (present = the family AI
 * payload carries that section, mirroring the individual page's tiles);
 * `hasCredit` is supplied by the loader, which checks for a TenCheck PDF blob
 * (a server read this client-safe module cannot do).
 */
export function deriveVerificationAvailability(
	aiReportData: Record<string, unknown> | null | undefined,
	hasCredit: boolean,
): MemberVerificationAvailability {
	const tiles = deriveVerificationTiles(aiReportData)
	return {
		id: tiles.id.present,
		income: tiles.income.present,
		rent: tiles.rent.present,
		credit: hasCredit,
	}
}

/** Household Court Records rollup (combined tile FACE, 2026-09-08): the
 * most actionable status across every member's own openroom summary —
 * any records found wins, then a run still pending, then a failed pull,
 * then a clean completed run, then paused; null when no member has any
 * court data (renders "Not Available"). Per-member detail lives in the
 * tile's popover, so the face carries the status only. */
const COURT_STATUS_PRIORITY: OpenroomSummaryStatus[] = [
	'records_found',
	'pending',
	'failed',
	'completed',
	'paused',
]

export function rollupHouseholdCourtRecords(
	summaries: Array<OpenroomSummary | null | undefined>,
): OpenroomSummaryStatus | null {
	for (const status of COURT_STATUS_PRIORITY) {
		if (summaries.some((summary) => summary?.status === status)) return status
	}
	return null
}

export interface MemberDocumentSummary {
	uploaded: number
	/** Checklist length (+ extra uploads) when the template lists documents;
	 * null for the uploads-only legacy shape. */
	total: number | null
	/** Accordion row summary: "2 of 3 uploaded" / "2 documents" / "No documents". */
	label: string
	/** The row has something to expand to — mirrors the individual card's
	 * render gate (uploads, or a configured checklist). */
	hasContent: boolean
}

/** Row summary for the combined Additional Documents accordion — the same
 * id-then-name matching the individual card (and form-gates
 * seedReviewDocuments) uses, so the count agrees with the rows inside. */
export function deriveMemberDocumentSummary(
	view: MemberDocuments,
): MemberDocumentSummary {
	const docs = Array.isArray(view.documents) ? view.documents : []
	const items = view.requiredItems ?? null
	if (!items || items.length === 0) {
		const uploaded = docs.length
		return {
			uploaded,
			total: null,
			label:
				uploaded === 0
					? 'No documents'
					: `${uploaded} document${uploaded === 1 ? '' : 's'}`,
			hasContent: uploaded > 0,
		}
	}
	const byId = new Map(docs.map((doc) => [doc.id, doc]))
	const byName = new Map(
		docs.map((doc) => [doc.name.trim().toLowerCase(), doc]),
	)
	const matchedIds = new Set<string>()
	let configuredUploaded = 0
	for (const item of items) {
		const match =
			byId.get(item.id) ?? byName.get(item.name.trim().toLowerCase()) ?? null
		if (!match) continue
		matchedIds.add(match.id)
		if (match.documentId || match.fileName) configuredUploaded += 1
	}
	const extras = docs.filter((doc) => !matchedIds.has(doc.id)).length
	const uploaded = configuredUploaded + extras
	const total = items.length + extras
	return {
		uploaded,
		total,
		label: `${uploaded} of ${total} uploaded`,
		hasContent: true,
	}
}

/** A co-resident slice for family-report eligibility. */
type FamilyEligibilityMember = {
	applicationType: GroupMember['applicationType']
	reportCompleted?: boolean
}

/**
 * How many co-residents (primary + co-applicants) have a finished individual
 * report and so may be combined. Guarantors are never counted here — use
 * {@link countEligibleGuarantors} for the student-housing composition gate.
 */
export function countEligibleCoResidents(
	members: FamilyEligibilityMember[],
): number {
	return members.filter(
		(m) => m.applicationType !== 'guarantor' && m.reportCompleted === true,
	).length
}

/** Completed guarantor reports in the household (student composition gate). */
export function countEligibleGuarantors(
	members: FamilyEligibilityMember[],
): number {
	return members.filter(
		(m) => m.applicationType === 'guarantor' && m.reportCompleted === true,
	).length
}

/** A co-resident / guarantor slice for the combined header's progress note. */
type FamilyProgressMember = {
	applicationType: GroupMember['applicationType']
	status: GroupMember['status']
}

function memberReportFinished(m: FamilyProgressMember): boolean {
	return m.status === 'completed' || m.status === 'accepted'
}

/**
 * Pre-completion progress for the combined header note. Counts CO-RESIDENTS
 * for the progress fraction. `eligible` uses the same composition rules as
 * family readiness: standard needs ≥2 co-residents; student housing needs
 * ≥1 co-resident and at least one guarantor with status completed or accepted.
 */
/**
 * How many submissions the combined report is waiting on, and how many people
 * it expects in total (client 2026-08-27) — returned together so the "N of M
 * people" line can never quote inconsistent numbers.
 *
 * `waiting` counts three things, because "not ready" has three causes and the
 * landlord reads one sentence for all of them:
 *   1. members who have applied but whose report isn't finished;
 *   2. people already INVITED who haven't started (they are expected, so they
 *      are part of the wait);
 *   3. the shortfall when the household is too small for a combined report
 *      even counting those invites — standard needs 2 co-residents, student
 *      housing 1 co-resident plus a completed guarantor.
 * Student housing additionally waits on its guarantor, since the report can't
 * be produced without one.
 *
 * `expected` is everyone that wait is measured against: applied members, live
 * invitations, and any shortfall. Children never appear — they hold no
 * application and submit nothing.
 */
export function combinedWaitingTally(input: {
	members: FamilyProgressMember[]
	pendingInvitees?: Array<{ invitationType: 'co_applicant' | 'guarantor' }>
	isStudentHousing?: boolean
}): { waiting: number; expected: number } {
	const invitees = input.pendingInvitees ?? []
	const coResidents = input.members.filter(
		(m) => m.applicationType !== 'guarantor',
	)
	const invitedCoResidents = invitees.filter(
		(i) => i.invitationType === 'co_applicant',
	).length
	const unfinishedCoResidents = coResidents.filter(
		(m) => !memberReportFinished(m),
	).length

	const minCoResidents = input.isStudentHousing ? 1 : 2
	const shortfall = Math.max(
		0,
		minCoResidents - (coResidents.length + invitedCoResidents),
	)
	let waiting = unfinishedCoResidents + invitedCoResidents + shortfall

	if (input.isStudentHousing) {
		// The report needs a FINISHED guarantor; count the outstanding one
		// (applied-but-unfinished, invited, or not yet asked for at all).
		const guarantors = input.members.filter(
			(m) => m.applicationType === 'guarantor',
		)
		if (!guarantors.some(memberReportFinished)) {
			const invitedGuarantors = invitees.filter(
				(i) => i.invitationType === 'guarantor',
			).length
			waiting += Math.max(
				1,
				guarantors.filter((m) => !memberReportFinished(m)).length +
					invitedGuarantors,
			)
		}
	}
	return {
		waiting,
		expected: input.members.length + invitees.length + shortfall,
	}
}

/** `waiting` alone — the count behind the unavailable line. */
export function combinedWaitingCount(input: {
	members: FamilyProgressMember[]
	pendingInvitees?: Array<{ invitationType: 'co_applicant' | 'guarantor' }>
	isStudentHousing?: boolean
}): number {
	return combinedWaitingTally(input).waiting
}

/**
 * The "Combined report unavailable" body line (client 2026-08-27). One
 * outstanding person reads as a single submission; two or more name the
 * fraction, because "2 of 4" tells the landlord how much of the household is
 * still missing in a way "2 more" does not. Zero returns null so the caller
 * shows nothing rather than "Waiting on 0".
 */
export function combinedWaitingCopy(
	remaining: number,
	expected?: number,
): string | null {
	if (!Number.isFinite(remaining) || remaining <= 0) return null
	const count = Math.floor(remaining)
	if (count === 1) {
		return 'Waiting on 1 more submission. The combined report generates automatically once it is in.'
	}
	// Never quote a total smaller than the wait itself.
	const total = Math.max(count, Math.floor(expected ?? 0))
	return `Waiting on ${count} of ${total} people to submit. The combined report generates automatically once everyone is in.`
}

export function deriveFamilyProgress(
	members: FamilyProgressMember[],
	options?: { isStudentHousing?: boolean },
): {
	eligible: boolean
	completed: number
	total: number
} {
	const isStudentHousing = options?.isStudentHousing === true
	const coResidents = members.filter((m) => m.applicationType !== 'guarantor')
	const hasCompletedGuarantor = members.some(
		(m) => m.applicationType === 'guarantor' && memberReportFinished(m),
	)
	const completed = coResidents.filter((m) => memberReportFinished(m)).length
	return {
		eligible: isStudentHousing
			? coResidents.length >= 1 && hasCompletedGuarantor
			: coResidents.length >= 2,
		completed,
		total: coResidents.length,
	}
}

/**
 * Whether to offer a (re)generate-FAMILY-report action on the combined page,
 * and which verb to show. The combined sibling of {@link deriveReportAction},
 * gated to exactly what `/resources/family-summary` will accept so the control
 * never appears when the endpoint would reject it:
 *   - `'regenerate'` — a completed family report exists; re-running REPLACES it.
 *   - `'generate'` — failed or missing family report (nothing to replace).
 * Returns null (hide) when: no regenerate permission, the household decision is
 * final (`decisionLocked` — DETAIL-V2-DECISION-RULES rule 3), a run is already
 * in flight (`'processing'`), composition is not ready (standard: <2 finished
 * co-residents; student: <1 finished applicant or <1 finished guarantor),
 * or any applicant is still on PRE-SCREENING (`preScreenApplicantCount`).
 */
export function deriveFamilyReportAction(input: {
	canRegenerate: boolean
	decisionLocked: boolean
	familyStatus: FamilyScoreSummary['processingStatus'] | null
	eligibleCoResidentCount: number
	/** Completed guarantors — required (≥1) when `isStudentHousing`. */
	eligibleGuarantorCount?: number
	isStudentHousing?: boolean
	preScreenApplicantCount?: number
	/**
	 * Student-housing gate: every co-resident must be linked to a guarantor.
	 * Omit / true for non-student households (no change).
	 */
	allApplicantsGuarantorLinked?: boolean
}): ReportActionMode | null {
	if (!input.canRegenerate) return null
	if (input.decisionLocked) return null
	if ((input.preScreenApplicantCount ?? 0) > 0) return null
	if (input.familyStatus === 'processing') return null
	const isStudentHousing = input.isStudentHousing === true
	const compositionReady = isStudentHousing
		? input.eligibleCoResidentCount >= 1 &&
			(input.eligibleGuarantorCount ?? 0) >= 1
		: input.eligibleCoResidentCount >= 2
	if (!compositionReady) return null
	if (input.allApplicantsGuarantorLinked === false) return null
	return input.familyStatus === 'completed' ? 'regenerate' : 'generate'
}

/**
 * Which family-report body to render — the combined-page twin of
 * `deriveDetailReportView` (display.ts), same three-way shape so the two
 * pages can't drift:
 * - `report`     — family predictions exist → render the summary + tiles.
 *                  A REgeneration keeps the old report on screen (mirrors
 *                  the individual page, which never blanks a report it has).
 * - `processing` — a run is in flight and there is nothing to show yet →
 *                  the "Generating Family Report" overlay + skeleton.
 * - `awaiting`   — nothing running and no predictions: never generated, or
 *                  the run FAILED → empty-state card (with the Generate CTA
 *                  when the caller offers one).
 * Member tiles / verification / timeline render independently — they are
 * per-member data, not family-report data (COMBINED-V2-DATA-MAP §7).
 */
export type CombinedReportView = 'report' | 'processing' | 'awaiting'

export function deriveCombinedReportView(input: {
	familyStatus: FamilyScoreSummary['processingStatus'] | null
	/** Parsed family predictions exist (`familyPredictions != null`). */
	hasReport: boolean
}): CombinedReportView {
	if (input.hasReport) return 'report'
	return input.familyStatus === 'processing' ? 'processing' : 'awaiting'
}

/**
 * Combined-header Accept/Decline visibility (client 2026-07-21): hide an
 * action once EVERY member (guarantors included) already carries that
 * decision — re-offering "Accept" on a fully-accepted household reads as a
 * bug, and the matrix would refuse every same→same row anyway. A mixed or
 * partly-undecided household keeps both buttons (the member-aware version
 * of the individual header's own-status rule, ApplicationHeader
 * showAccept/showDecline). Empty member lists keep both (solo pages don't
 * use this — the entry application is the household there).
 */
export function deriveHouseholdFullyDecided(
	members: ReadonlyArray<{ status: string }>,
): { allAccepted: boolean; allDeclined: boolean } {
	return {
		allAccepted:
			members.length > 0 &&
			members.every((member) => member.status === 'accepted'),
		allDeclined:
			members.length > 0 &&
			members.every((member) => member.status === 'declined'),
	}
}

/**
 * A family run that has been 'processing' longer than this is treated as
 * FAILED (client 2026-09-07): the worker died or the row was hand-reset, and
 * the page used to spin on the "Generating…" overlay forever with no way out.
 * 15 minutes is well past any real run (the worker completes in well under
 * two) and matches the guarantor sweep cadence.
 */
export const FAMILY_RUN_STALE_MS = 15 * 60 * 1000

/**
 * Loader-side status resolution: 'processing' past FAMILY_RUN_STALE_MS →
 * 'failed'; everything else passes through. A missing start time can't be
 * judged, so it stays 'processing' (a row is stamped on insert, so this is
 * the legacy-row case only).
 */
export function resolveFamilyProcessingStatus(input: {
	status: FamilyScoreSummary['processingStatus']
	processingStartedAt: Date | string | null
	now: Date
}): FamilyScoreSummary['processingStatus'] {
	if (input.status !== 'processing' || !input.processingStartedAt) {
		return input.status
	}
	const started = new Date(input.processingStartedAt).getTime()
	if (!Number.isFinite(started)) return input.status
	return input.now.getTime() - started > FAMILY_RUN_STALE_MS
		? 'failed'
		: input.status
}

export type CombinedHeaderState =
	| { kind: 'generating' }
	| { kind: 'upsell' }
	| { kind: 'unavailable'; body: string }

/**
 * The combined header's pre-completion note (client 2026-09-07). One
 * decision tree, first match wins:
 * 1. a run in flight → "generating";
 * 2. anyone still to submit → UNAVAILABLE with the waiting tally;
 * 3. everyone in but some on pre-screening → the upgrade offer;
 * 4. everyone in, nothing running → UNAVAILABLE with a status line: a FAILED
 *    run says so (and points at the actions menu when a regenerate is
 *    offered) — it used to read "generates automatically once every member
 *    is in", which was false with every member already in.
 */
export function deriveCombinedHeaderState(input: {
	familyStatus: FamilyScoreSummary['processingStatus'] | null
	waiting: number
	expected: number
	preScreenApplicantCount: number
	/** A (re)generate action is offered (deriveFamilyReportAction ≠ null). */
	regenerateOffered: boolean
}): CombinedHeaderState {
	if (input.familyStatus === 'processing') return { kind: 'generating' }
	if (input.waiting > 0) {
		return {
			kind: 'unavailable',
			body:
				combinedWaitingCopy(input.waiting, input.expected) ??
				'Waiting on submissions. The combined report generates automatically once everyone is in.',
		}
	}
	if (input.preScreenApplicantCount > 0) return { kind: 'upsell' }
	if (input.familyStatus === 'failed') {
		return {
			kind: 'unavailable',
			body: input.regenerateOffered
				? "The combined report could not be generated. Each member's individual report is unaffected — use Generate Report in the actions menu to try again."
				: "The combined report could not be generated. Each member's individual report is unaffected and still available below.",
		}
	}
	return {
		kind: 'unavailable',
		body: 'Every member has submitted. The combined report generates automatically once their individual reports are ready.',
	}
}
